import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import StatusContainer from '@/components/StatusContainer';
import { ACCENT, ACCENT_TINT, accentAlpha } from '@/components/Landing/landingTokens';
import { isDarkTheme } from '@/constants/Colors';

const STEPS = [
  { icon: 'pen-nib', key: 'pages.agents.intro.step1' },
  { icon: 'wand-magic-sparkles', key: 'pages.agents.intro.step2' },
  { icon: 'comments', key: 'pages.agents.intro.step3' },
];

export default function BotsPersonalSection({ hasUser, count, isLoading, error, onRetry, children }) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const dark = isDarkTheme(theme);
  const brand = dark ? ACCENT_TINT : ACCENT;
  const styles = useMemo(() => createStyles(colors, isXS, dark), [colors, isXS, dark]);
  const hasBots = hasUser && count > 0;

  return (
    <View style={styles.section} testID="bots-personal-section">
      <View style={styles.heading}>
        <View style={styles.title}>
          <Text tag="h2" size="xl" weight="bold">{t('pages.agents.myAgents')}</Text>
          {hasBots && <Text size="s" color="tertiary">{count}</Text>}
        </View>
      </View>
      {hasUser && error ? (
        <StatusContainer type="error" message={error} actionText={t('pages.common.retry')} onAction={onRetry} />
      ) : hasUser && (isLoading || hasBots) ? children : (
        <View style={styles.empty}>
          <View style={styles.emptyIntro}>
            <View style={styles.icon}><Icon name="robot" size={24} color={brand} /></View>
            <View style={styles.emptyCopy}>
              <Text size="l" weight="bold">{t('pages.agents.intro.createTitle')}</Text>
              <Text size="n" color="secondary" style={styles.body}>{t('pages.agents.noAgentsYet')}</Text>
            </View>
          </View>
          <View style={styles.steps}>
            {STEPS.map((step, index) => (
              <View key={step.key} style={styles.step}>
                <View style={styles.stepIcon}>
                  <Icon name={step.icon} size={14} color={brand} />
                </View>
                <View style={styles.stepCopy}>
                  <Text size="xs" weight="semibold" style={{ color: brand }}>{index + 1}</Text>
                  <Text size="s" color="secondary" style={styles.stepText}>{t(step.key)}</Text>
                </View>
              </View>
            ))}
          </View>
          {!hasUser && <Text size="s" color="tertiary" style={styles.hint}>{t('pages.agents.intro.signInHint')}</Text>}
        </View>
      )}
    </View>
  );
}

const createStyles = (colors, compact, dark) => StyleSheet.create({
  section: { gap: 18 },
  heading: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  title: { flexDirection: 'row', alignItems: 'center', gap: 10, flexShrink: 1 },
  empty: {
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    borderRadius: 20,
    padding: compact ? 20 : 28,
    gap: 22,
  },
  emptyIntro: { flexDirection: 'row', alignItems: 'flex-start', gap: 16 },
  icon: {
    width: 52,
    height: 52,
    borderRadius: 16,
    backgroundColor: accentAlpha(dark ? 0.20 : 0.09, dark),
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyCopy: { flex: 1, minWidth: 0, gap: 6 },
  body: { lineHeight: 23, maxWidth: 620 },
  steps: { flexDirection: compact ? 'column' : 'row', gap: compact ? 10 : 12 },
  step: {
    flex: compact ? undefined : 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: compact ? 12 : 14,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_primary,
  },
  stepIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: accentAlpha(dark ? 0.20 : 0.09, dark),
  },
  stepCopy: { flex: 1, minWidth: 0, gap: 4 },
  stepText: { lineHeight: 21 },
  hint: { flexShrink: 1 },
});
